import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { VwTone } from '../status-badge/status-badge.component';

// One part of the optional "16 checked out · 2 inside" line under a tile's
// value. clickable parts are their own click target (emitting `key`) and do
// not trigger the tile's own click; non-clickable parts are plain text.
export interface VwTileSubLink {
  text: string;
  key: string;
  clickable?: boolean;
}

@Component({
  selector: 'app-stat-tile',
  templateUrl: './stat-tile.component.html',
  styleUrls: ['./stat-tile.component.scss'],
})
export class StatTileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() icon: string;
  @Input() label: string;
  @Input() value: string;
  // Optional lighter/muted second number next to value (e.g. "17" next to
  // a bold "63") — only set where a real paired number exists, never
  // fabricated just to match the two-tone layout.
  @Input() secondaryValue: string | null = null;
  @Input() sublabel: string;
  @Input() tone: VwTone = 'indigo';
  @Input() clickable = false;
  @Input() route: any[] | string;
  // Optional query string for `route` (e.g. { when: 'today' } to open a list already filtered).
  @Input() routeQuery: { [key: string]: string } | null = null;
  // Optional arrival-rate ring (e.g. Current/Expected Visitor tiles) — only
  // rendered when the tile has a real ratio behind it, never a fake ring.
  @Input() percent: number | null = null;
  // Optional hover helper — a one-line explanation of what the tile means
  // (and, where relevant, the arithmetic behind it, e.g. "163 arrived of
  // 194 expected today"). Opt-in: tiles that don't set this show no popup,
  // so every other existing usage of this shared component is unaffected.
  @Input() helperText: string | null = null;
  // Open the helper popup under the tile instead of above it. For the top row of a page, where "above" is the
  // header (the popup slid underneath it and could not be read).
  @Input() popupBelow = false;
  // Optional real day-over-day comparison (e.g. today's arrived count minus
  // yesterday's, from the backend — never estimated/fabricated client-side).
  // Opt-in: only tiles that pass this show a delta badge at all.
  @Input() delta: number | null = null;
  @Input() deltaLabel = 'vs yesterday';
  // Optional split line (e.g. "16 checked out · 2 inside") where each part can
  // open its own filtered list. Opt-in: tiles that don't pass it are unchanged.
  @Input() subLinks: VwTileSubLink[] | null = null;
  @Output() subLinkClick = new EventEmitter<string>();
  @Output() tileClick = new EventEmitter<void>();

  get hasDelta(): boolean {
    return this.delta !== null && this.delta !== undefined && !isNaN(this.delta) && this.delta !== 0;
  }

  get deltaIsUp(): boolean {
    return (this.delta || 0) > 0;
  }

  // Animated copies of the raw inputs — the template renders these, not the
  // inputs directly, so a KPI landing (or a later live-refresh) counts up
  // instead of snapping straight to the final number. Only plain integers
  // animate (guarded by NUMERIC_PATTERN); anything else — and there is no
  // such usage today, but this keeps future non-numeric tiles safe — just
  // renders as-is with no animation.
  displayValue = '';
  displaySecondaryValue: string | null = null;
  displayPercent = 0;

  private static readonly NUMERIC_PATTERN = /^-?\d+$/;
  private static readonly DURATION_MS = 700;
  private initialized = false;
  private valueFrame: number | null = null;
  private secondaryFrame: number | null = null;
  private percentFrame: number | null = null;

  constructor(private router: Router) {}

  get isClickable(): boolean {
    return this.clickable || !!this.route;
  }

  get hasPercent(): boolean {
    return this.percent !== null && this.percent !== undefined && !isNaN(this.percent);
  }

  get clampedPercent(): number {
    return Math.max(0, Math.min(100, this.displayPercent || 0));
  }

  // SVG progress ring — viewBox 0 0 52 52, center (26,26), r=20 (matches
  // the approved reference exactly: track + arc stroked circles, dashArray
  // driven off the real percent, dot marker at the arc's leading edge).
  private readonly ringCenter = 26;
  private readonly ringRadius = 20;

  get ringCircumference(): number {
    return 2 * Math.PI * this.ringRadius;
  }

  get ringDashArray(): string {
    const filled = (this.clampedPercent / 100) * this.ringCircumference;
    return `${filled} ${this.ringCircumference}`;
  }

  get ringDotX(): number {
    const angle = (this.clampedPercent / 100) * 2 * Math.PI;
    return this.ringCenter + this.ringRadius * Math.sin(angle);
  }

  get ringDotY(): number {
    const angle = (this.clampedPercent / 100) * 2 * Math.PI;
    return this.ringCenter - this.ringRadius * Math.cos(angle);
  }

  get accentColor(): string {
    return `var(--vw-${this.tone}-text)`;
  }

  ngOnInit(): void {
    this.displayValue = this.value;
    this.displaySecondaryValue = this.secondaryValue;
    this.displayPercent = this.percent || 0;
    this.runCountUp(this.value, null, v => (this.displayValue = v), f => (this.valueFrame = f), () => this.valueFrame);
    if (this.secondaryValue) {
      this.runCountUp(this.secondaryValue, null, v => (this.displaySecondaryValue = v), f => (this.secondaryFrame = f), () => this.secondaryFrame);
    }
    if (this.hasPercent) {
      this.runNumberTween(0, this.clampedPercentInput, n => (this.displayPercent = n), f => (this.percentFrame = f), () => this.percentFrame);
    }
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return; // ngOnInit handles the first paint
    }
    if (changes.value && changes.value.currentValue !== changes.value.previousValue) {
      this.runCountUp(this.value, changes.value.previousValue, v => (this.displayValue = v), f => (this.valueFrame = f), () => this.valueFrame);
    }
    if (changes.secondaryValue && changes.secondaryValue.currentValue !== changes.secondaryValue.previousValue) {
      this.runCountUp(this.secondaryValue || '', changes.secondaryValue.previousValue, v => (this.displaySecondaryValue = v), f => (this.secondaryFrame = f), () => this.secondaryFrame);
    }
    if (changes.percent && changes.percent.currentValue !== changes.percent.previousValue && this.hasPercent) {
      this.runNumberTween(this.displayPercent, this.clampedPercentInput, n => (this.displayPercent = n), f => (this.percentFrame = f), () => this.percentFrame);
    }
  }

  ngOnDestroy(): void {
    [this.valueFrame, this.secondaryFrame, this.percentFrame].forEach(f => {
      if (f !== null) {
        cancelAnimationFrame(f);
      }
    });
  }

  private get clampedPercentInput(): number {
    return Math.max(0, Math.min(100, this.percent || 0));
  }

  // Parses a tile value into an integer when it's one (and only when it is)
  // so count-up animation never misinterprets non-numeric tile content.
  private parseNumeric(raw: string | null | undefined): number | null {
    if (raw === null || raw === undefined) {
      return null;
    }
    const trimmed = raw.trim();
    return StatTileComponent.NUMERIC_PATTERN.test(trimmed) ? parseInt(trimmed, 10) : null;
  }

  private runCountUp(
    rawTarget: string,
    rawFrom: string | null | undefined,
    apply: (text: string) => void,
    setFrame: (id: number | null) => void,
    getFrame: () => number | null
  ): void {
    const target = this.parseNumeric(rawTarget);
    if (target === null) {
      apply(rawTarget); // non-numeric content — show as-is, no animation
      return;
    }
    const from = this.parseNumeric(rawFrom) ?? 0;
    this.runNumberTween(from, target, n => apply(n.toString()), setFrame, getFrame, () => apply(rawTarget));
  }

  private runNumberTween(
    from: number,
    to: number,
    apply: (n: number) => void,
    setFrame: (id: number | null) => void,
    getFrame: () => number | null,
    onDone?: () => void
  ): void {
    const existing = getFrame();
    if (existing !== null) {
      cancelAnimationFrame(existing);
    }
    if (from === to) {
      apply(to);
      if (onDone) {
        onDone();
      }
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / StatTileComponent.DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      apply(Math.round(from + (to - from) * eased));
      if (t < 1) {
        setFrame(requestAnimationFrame(step));
      } else {
        setFrame(null);
        if (onDone) {
          onDone();
        }
      }
    };
    setFrame(requestAnimationFrame(step));
  }

  // The dashboard rebuilds its tile objects on every change-detection pass, so
  // without a stable identity the sub-link <span>s would be destroyed and
  // recreated between mouse-down and mouse-up, and the browser would never
  // fire a click on them.
  trackByLinkKey(index: number, link: VwTileSubLink): string {
    return link.key;
  }

  onSubLinkClick(event: Event, link: VwTileSubLink): void {
    if (!link.clickable) {
      return; // plain text — let the click fall through to the tile itself
    }
    event.stopPropagation();
    this.subLinkClick.emit(link.key);
  }

  onClick(): void {
    if (this.route) {
      this.router.navigate(Array.isArray(this.route) ? this.route : [this.route],
        this.routeQuery ? { queryParams: this.routeQuery } : undefined);
    } else if (this.clickable) {
      this.tileClick.emit();
    }
  }
}
